import { Stack, TextField } from '@mui/material'
import type { FormikProps } from 'formik'
import { NumberedSectionCard } from '~/shared/ui'
import type { PharmacyField, PolicyEditorValues } from './policyEditorForm'

interface PharmacySectionProps {
  formik: FormikProps<PolicyEditorValues>
  readOnly: boolean
}

// `satisfies` rather than a bare literal: the labels are ours, the names are the endpoint's, so a
// field renamed backend-side fails here instead of being posted under a key it ignores.
type PharmacyTextField = { name: Exclude<PharmacyField, 'waiver_disclaimer'>; label: string }

const PBM_FIELDS = [
  { name: 'pbm_name', label: 'PBM name' },
  { name: 'pbm_url', label: 'PBM URL' },
] as const satisfies readonly PharmacyTextField[]

const RX_FIELDS = [
  { name: 'rx_bin', label: 'Rx BIN' },
  { name: 'rx_pcn', label: 'Rx PCN' },
  { name: 'rx_group', label: 'Rx group' },
] as const satisfies readonly PharmacyTextField[]

/** Section 3: pharmacy benefit manager details, as they appear on the member's ID card. */
export const PharmacySection = ({ formik, readOnly }: PharmacySectionProps) => (
  <NumberedSectionCard index={3} title="Pharmacy & PBM" testId="section-pharmacy">
    <Stack spacing={2}>
      {[PBM_FIELDS, RX_FIELDS].map((row, index) => (
        <Stack key={index} direction="row" spacing={2}>
          {row.map(field => (
            <TextField
              key={field.name}
              fullWidth
              name={field.name}
              label={field.label}
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              disabled={readOnly}
              data-testid={`editor-${field.name}`}
            />
          ))}
        </Stack>
      ))}
      <TextField
        multiline
        minRows={2}
        name="pharmacy_disclaimer"
        label="Pharmacy disclaimer"
        value={formik.values.pharmacy_disclaimer}
        onChange={formik.handleChange}
        disabled={readOnly}
        data-testid="editor-pharmacy_disclaimer"
      />
    </Stack>
  </NumberedSectionCard>
)
